'use client';

import { ComponentProps, DefaultPageProps, Themes } from '@/lib/types/shared/page.types';
import { Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { Image } from '@/components/shared';

type Props = {
    items: {
        title: string,
        text: string,
        icon?: {
            src: string,
            alt: string,
            width: number,
            height: number,
        },
        link: string
    }[],
    hideOnMobile?: boolean,
    alignment?: 'horizontal' | 'vertical',
    animationDuration?: number,
    theme?: Themes
} & ComponentProps & DefaultPageProps;

export default function InfoBlocks({ items, hideOnMobile = false, alignment = 'horizontal', animationDuration = 3000, theme = 'light', className = '', params }: Props) {
    const t = useTranslations();

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [intervalPaused, setIntervalPaused] = useState<boolean>(false);
    const [active, setActive] = useState<number>(0);

    function handleInterval() {
        setActive(getNextIndex(active));
    }

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(handleInterval, animationDuration);

        if (intervalPaused) {
            pauseInterval();
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [active]);

    const pauseInterval = () => {
        if (intervalRef.current) {
            setIntervalPaused(true);
            clearInterval(intervalRef.current);
        }
    };

    const resumeInterval = () => {
        if (intervalRef.current) {
            setIntervalPaused(false);
            intervalRef.current = setInterval(handleInterval, animationDuration);
        }
    };

    function needsProgress(index: number) {
        return index === getNextIndex(active);
    }

    function getNextIndex(index: number) {
        if (index + 1 < items.length) {
            return index + 1;
        } else {
            return 0;
        }
    }

    return (
        <>
            <div data-interval-paused={intervalPaused} className={`info-blocks -mt-16 w-fit ${hideOnMobile ? 'hidden md:flex' : 'flex'} ${alignment == 'vertical' ? 'flex-col' : 'flex-row'} gap-3 cursor-default ${className}`} onMouseEnter={pauseInterval} onMouseLeave={resumeInterval}>
                {items.map((item, index) => (
                    <div key={index} onClick={() => setActive(index)} data-animate-progress={needsProgress(index)} data-item-active={index === active} className='flex flex-col gap-y-2 w-full px-4 py-3 bg-gradient-to-r from-white-t-10 to-transparent backdrop-blur-2xl relative rounded-lg'>
                        <div data-item-progress style={{
                            animationDuration: `${animationDuration}ms!important`
                        }} className="absolute top-0 left-0 z-1 h-full bg-gradient-to-r from-white-t-10 to-transparent backdrop-blur-2xl transition-all duration-300 rounded-lg"></div>

                        <div className='relative z-10'>
                            <div className='flex gap-x-2 justify-between'>
                                <Typography variant='h5' component='h2' className={`${theme == 'dark' ? 'text-primary-gray-950' : 'text-white'} capitalize font-semibold`}>
                                    {item.title}
                                </Typography>

                                {item.icon && (
                                    <div className={`${theme == 'dark' ? 'bg-white' : 'bg-primary-gray-950'} w-fit h-full px-4 py-1 relative rounded-full flex items-center justify-center`}>
                                        <Typography variant='body1' className={`${theme == 'dark' ? 'text-primary-gray-950' : 'text-primary-yellow-400'} capitalize font-semibold`}>
                                            {t('read')}
                                        </Typography>

                                        <div className="absolute -bottom-2 -right-2">
                                            <svg className="w-6 h-6" id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 109.21 122.88"><title>mouse-click</title><path className={`${theme == 'dark' ? 'fill-primary-gray-950' : 'fill-white'}`} d="M86,122.31a5.57,5.57,0,0,1-.9.35,5.09,5.09,0,0,1-1,.18,5.46,5.46,0,0,1-1,0,6.77,6.77,0,0,1-1-.15,6,6,0,0,1-1-.36l0,0a5.51,5.51,0,0,1-.92-.53l0,0a6.41,6.41,0,0,1-.78-.69,5.19,5.19,0,0,1-.65-.87l-9.08-14.88-7.69,9a15.49,15.49,0,0,1-1.1,1.18c-.39.37-.78.71-1.18,1l-.08.06a12.19,12.19,0,0,1-1.2.82,9.66,9.66,0,0,1-1.24.63,6.91,6.91,0,0,1-1,.37,6.21,6.21,0,0,1-1,.22,7.55,7.55,0,0,1-1.06.07,7.19,7.19,0,0,1-1-.11,6.14,6.14,0,0,1-1.18-.35,5.42,5.42,0,0,1-1.06-.57,6.22,6.22,0,0,1-.92-.78l0,0a7.31,7.31,0,0,1-.75-1l-.11-.2-.09-.21L47.72,112l0-.17L40.91,43.26a4.52,4.52,0,0,1,0-1.33,4.3,4.3,0,0,1,.43-1.25,4.31,4.31,0,0,1,1.39-1.55l0,0a3.82,3.82,0,0,1,.9-.46,4.25,4.25,0,0,1,1-.24h0a4.31,4.31,0,0,1,1.29.05,4.67,4.67,0,0,1,1.25.44l.3.16c13.51,8.84,26.1,17.06,38.64,25.25l19,12.39a11.72,11.72,0,0,1,1,.72l0,0a8.78,8.78,0,0,1,.82.73l.06.07a7.41,7.41,0,0,1,.71.82,5.91,5.91,0,0,1,.57.87,6.42,6.42,0,0,1,.51,1.14,5.6,5.6,0,0,1,.26,1.17,5.44,5.44,0,0,1,0,1.21h0a6.59,6.59,0,0,1-.23,1.19,6.54,6.54,0,0,1-.94,1.88,6.41,6.41,0,0,1-.67.83,7.45,7.45,0,0,1-.82.76,10.42,10.42,0,0,1-1.16.83,12.92,12.92,0,0,1-1.34.7c-.47.21-1,.41-1.46.58a14.27,14.27,0,0,1-1.55.43h0c-2.77.54-5.53,1.21-8.27,1.87l-3.25.77,9,14.94a5.84,5.84,0,0,1,.46,1,5.59,5.59,0,0,1,.15,3.21l0,.1a5.53,5.53,0,0,1-.33.94,6.43,6.43,0,0,1-.51.89,5.62,5.62,0,0,1-.68.81,6,6,0,0,1-.82.67l-2,1.29A83,83,0,0,1,86,122.31ZM37.63,19.46a4,4,0,0,1-6.92,4l-8-14a4,4,0,0,1,6.91-4l8.06,14Zm-15,46.77a4,4,0,0,1,4,6.91l-14,8.06a4,4,0,0,1-4-6.91l14-8.06ZM20.56,39.84a4,4,0,0,1-2.07,7.72L3,43.36A4,4,0,0,1,5,35.64l15.53,4.2ZM82,41.17a4,4,0,0,1-4-6.91L92,26.2a4,4,0,0,1,4,6.91L82,41.17ZM63.46,20.57a4,4,0,1,1-7.71-2.06L59.87,3A4,4,0,0,1,67.59,5L63.46,20.57Zm20.17,96.36,9.67-5.86c-3.38-5.62-8.85-13.55-11.51-19.17a2.17,2.17,0,0,1-.12-.36,2.4,2.4,0,0,1,1.81-2.87c5.38-1.23,10.88-2.39,16.22-3.73a10.28,10.28,0,0,0,1.8-.58,6.11,6.11,0,0,0,1.3-.77,3.38,3.38,0,0,0,.38-.38.9.9,0,0,0,.14-.24l-.06-.18a2.15,2.15,0,0,0-.44-.53,5.75,5.75,0,0,0-.83-.63L47.06,45.75c2.11,21.36,5.2,44.1,6.45,65.31a6.28,6.28,0,0,0,.18,1,2.89,2.89,0,0,0,.26.62l.13.14a1,1,0,0,0,.29,0,2.76,2.76,0,0,0,.51-.17,5.71,5.71,0,0,0,1.28-.79,11.22,11.22,0,0,0,1.35-1.33c1.93-2.27,9.6-12.14,11.4-13.18a2.4,2.4,0,0,1,3.28.82l11.44,18.75Z" /></svg>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div data-item-text className={`overflow-hidden transition-all duration-1000`}>
                                <Typography variant='body1' className={`${theme == 'dark' ? 'text-primary-gray-950' : 'text-white'} mt-1`}>
                                    {item.text}
                                </Typography>

                                {item.link && (
                                    <Link href={item.link} className={`w-fit group ${theme == 'dark' ? 'text-primary-gray-950' : 'text-white'} font-semibold mt-2 flex items-center gap-x-2`}>
                                        {t('view')}

                                        <Image
                                            src='Icons/arrow-primary_ikvnap'
                                            alt='arrow'
                                            width={16}
                                            height={16}
                                            className='-rotate-45 group-hover:rotate-0 transition-all duration-300'
                                        />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}
